import * as React from 'react';
import { FocusableElement, tabbable } from 'tabbable';
import { Key } from 'ts-key-enum';

interface FocusTrapProps {
    readonly enabled: boolean;
    readonly applyActiveElement?: boolean;
}

export class FocusTrap extends React.Component<FocusTrapProps> {
    private modal = React.createRef<HTMLDivElement>();
    private enabled = false;
    private firstActiveElement: Element | undefined;
    private isShiftActive = false;

    componentDidMount(): void {
        if (this.props.enabled || this.props.enabled == undefined) this.enable();
    }

    componentWillUnmount(): void {
        this.disable();
    }

    componentDidUpdate(): void {
        if (this.props.enabled || this.props.enabled == undefined) this.enable();
        else this.disable();
    }

    private enable() {
        if (this.enabled) return;

        this.handleInitiatorFocusOut();
        this.enabled = true;
        document.addEventListener('focusin', this.onFocusIn);
        document.addEventListener('keydown', this.handleShiftDown);
        document.addEventListener('keyup', this.handleShiftUp);
    }

    private disable() {
        if (this.enabled) {
            document.removeEventListener('focusin', this.onFocusIn);
            document.removeEventListener('keydown', this.handleShiftDown);
            document.removeEventListener('keyup', this.handleShiftUp);
            this.firstActiveElement?.removeEventListener('keydown', this.onInitiatorFocusOut);
        }

        this.enabled = false;
    }

    private handleShiftDown = (e: KeyboardEvent) => {
        if (e.key === Key.Shift) this.isShiftActive = true;
    };

    private handleShiftUp = (e: KeyboardEvent) => {
        if (e.key === Key.Shift) this.isShiftActive = false;
    };

    private getTabbableDescendantsOf = (): FocusableElement[] => {
        if (this.modal.current) {
            const tabbableElements = tabbable(this.modal.current);
            this.applyFirstElement(tabbableElements);

            return tabbableElements;
        }

        return [];
    };

    private isDescendant = (element: EventTarget | null) => {
        return this.modal.current && this.modal.current.contains(element as Node);
    };

    private onFocusIn = (e: FocusEvent) => {
        e.preventDefault();

        const tabbables = this.getTabbableDescendantsOf();

        if (tabbables.length === 0) return;

        const firstTabbable = tabbables[0];
        const lastTabbable = tabbables[tabbables.length - 1];

        if (e.relatedTarget === firstTabbable && this.isShiftActive) {
            lastTabbable.focus();
        } else if ((e.relatedTarget === lastTabbable && !this.isShiftActive) || (!this.isDescendant(e.target) && !this.props.applyActiveElement)) {
            firstTabbable.focus();
        }
    };

    private handleInitiatorFocusOut = () => {
        if (document.activeElement) {
            this.firstActiveElement = document.activeElement;

            this.firstActiveElement.addEventListener('keydown', this.onInitiatorFocusOut);
        }
    };

    private onInitiatorFocusOut = (e: Event) => {
        const event = e as KeyboardEvent;

        if (event.shiftKey && event.key === Key.Tab && this.modal.current) {
            event.preventDefault();
            const tabbableElements = tabbable(this.modal.current);
            tabbableElements[tabbableElements.length - 1].focus();
        }
    };

    private applyFirstElement = (arr: FocusableElement[]) => {
        this.firstActiveElement && this.props.applyActiveElement && arr.unshift(this.firstActiveElement as HTMLElement);
    };

    render() {
        // eslint-disable-next-line react/prop-types
        return <div ref={this.modal}>{this.props.children}</div>;
    }
}
